import Box from '../components/Box';
import Container from '../components/Container';
import Row from '../components/Row';
import ShoppingCart from '../components/ShoppingCart';
import FullWidthLayout from '../layouts/FullWidthLayout';
import { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate, Navigate } from 'react-router-dom';
import Button from '@mui/joy/Button';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';
import * as Api from '../services/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { getCheckoutURL } from '../services/stripe';
import NotFound from '../components/NotFound';
import { FormControl, FormLabel, Grid, Input } from '@mui/joy';
import useSessionStorage from '../hooks/useSessionStorage';
import PhoneInput, { isPossiblePhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import '../components/CopyCatInput.css';
import { validateEmail } from '../utils/emailValidator';


const BrandsCheckoutPage = () => {
	const [cart, setCart] = useSessionStorage();
	const [loading, setLoading] = useState(false);
	const [layout, setLayout] = useState('center'); // show terms of service modal at the open
	const [didAgree, setDidAgree] = useState(true);
	const [isValidSession, setIsValidSession] = useState(false);
	const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
	const [buttonTitle, setButtonTitle] = useState('Checkout with Stripe');
	const [disableCheckoutButton, setDisableCheckoutButton] = useState(false);
	const [ownerFullName, setOwnerFullName] = useState('');
	const [ownerPhone, setOwnerPhone] = useState('');
	const [ownerEmail, setOwnerEmail] = useState('');
	const [makeChanges, setMakeChanges] = useState(false);
	const [canSubmitChanges, setCanSubmitChanges] = useState(false);
	const [processingNewChanges, setProcessingNewChanges] = useState(false);
	const [loadingText, setLoadingText] = useState('Loading');

	const location = useLocation();
	const navigate = useNavigate();
	const queryParams = new URLSearchParams(location.search);
	const accessCode = queryParams.get('accessCode');
	const commaSeparatedListOfTags = queryParams.get('tags');

	// TODO: what makes more sense? stripe id or human readable code?
	const discountId = queryParams.get('discountId');

	useEffect(() => {
		if (ownerFullName.length > 0 && isPossiblePhoneNumber(ownerPhone || '') && validateEmail(ownerEmail|| '')) {
			setCanSubmitChanges(true);
		} else {
			setCanSubmitChanges(false);
		}
	}, [ownerFullName, ownerPhone, ownerEmail]);

	useEffect(() => {
		async function doIt() {
			setLoading(true);
			// TODO: Check whether user is authorized
			// 1. If so, confirm email associated
			const authorizationPromise = Api.authorizeOnce(commaSeparatedListOfTags, accessCode);
			try {
				const [authorization] = await Promise.all([authorizationPromise]);
				if (authorization.isValid) {
					const tagsForCart = [];
					const decodedListOfTags = decodeURIComponent(commaSeparatedListOfTags);
					const tags = decodedListOfTags.split(',');

					if (tags.length > 25) {
						Notification.error('You can only claim up to 25 tags at a time.');
						return;
					}

					for (const tag of tags) {
						setIsValidSession(true);
						setDidAgree(false);

						const tagObject = await Api.getTag(tag);

						if (tagObject.stripeReferenceId) {
							continue;
						}

						setOwnerFullName(tagObject.ownerName);
						setOwnerPhone(tagObject.ownerPhone);
						setOwnerEmail(tagObject.ownerEmail);

						const tagForCart = {
							...tagObject,
							text: tag,
							...(discountId && { discountId: parseFloat(discountId || 0.0) })
						};
						tagsForCart.push(tagForCart);
					}
					const newCart = await Api.addOneOrMoreBrandsToCart(tagsForCart);
					setCart(newCart);
				} else {
					// 2. Otherwise,
					// a. If bought: display tag purchase status maybe a thank you note

					// b. If time expired: say access code expired
				}
			} catch (e) {
				console.log(e);
			}
			setLoading(false);
			// TODO: Check for hold expire time as well
		}
		doIt();

		// TODO: Get and update prices in cart
		// 1. Hit price endpoint

		// 2. Hit discount endpoint

		return () => {
			setMakeChanges(false);
		};
	}, []);

	useEffect(() => {
		if (!loading && isValidSession && !!commaSeparatedListOfTags.length && !cart.tags?.length) {
			navigate(`/purchase-confirmation?claimed=true&tags=${commaSeparatedListOfTags}`);
		}
	}, [loading, isValidSession, commaSeparatedListOfTags, cart, navigate]);

	useEffect(() => {
		// after 5 seconds, change the loading text to "Still loading"
		setTimeout(() => {
			setLoadingText('Still loading');
		}, 3000);
	}, []);

	function agreeHandler(event) {
		event.preventDefault();
		setDidAgree(true);
		setOpenConfirmationModal(true);
	}

	function disagreeHandler(event) {
		event.preventDefault();
		window.location.assign('https://andme.org');
	}

	const handleCheckoutButton = async () => {
		setButtonTitle('Processing...');
		setDisableCheckoutButton(true);
		const checkoutUrl = await getCheckoutURL(cart, setCart);
		window.location.assign(checkoutUrl);
	};

	if (loading) {
		return (
			<FullWidthLayout>
				<Container>
					<Box>
						<Row justifyContent='center'>
							<Typography>
								{loadingText}
								&nbsp;
								<FontAwesomeIcon icon={faSpinner} spin={true} />
							</Typography>
						</Row>
					</Box>
				</Container>
			</FullWidthLayout>
		);
	}

	if (!loading && !isValidSession) {
		return (
			<NotFound />
		);
	}

	return (
		<FullWidthLayout>
			<Container>
				<Grid container spacing={10} sx={{ flexGrow: 1 }}>
					<Grid xs={12} md={6}>
						<Typography level="h2">Checkout</Typography>
						<Row flexDirection="column">
							<Button
								onClick={handleCheckoutButton}
								startDecorator={<FontAwesomeIcon icon={faLock} />}
								disabled={loading || disableCheckoutButton}
							>{buttonTitle}</Button>
							<Typography level="body2">Complete your purchase safely and securely with <a href='https://stripe.com/' target={'_blank'} rel="noreferrer">Stripe</a>. Once payment is successful, you'll be redirected back here. If you run into any issues, please <a>contact us.</a></Typography>
						</Row>
					</Grid>
					<Grid xs={12} md={6}>
						<Typography level="h2">Cart</Typography>
						<ShoppingCart
							cart={cart}
							isCartLoading={loading}
							setIsCartLoading={setLoading}
							onRemoveFromCart={() => { }}
						/>
					</Grid>
				</Grid>
				<Modal
					open={!didAgree}
					onClose={() => {
						setLayout(undefined);
					}}
				>
					<ModalDialog aria-labelledby="dialog-vertical-scroll-title" layout={layout}>
						<Typography id="dialog-vertical-scroll-title" component="h2">
							Terms and Conditions
						</Typography>
						<Stack spacing={1}>
							<Typography level="body2" >
								Do you agree with our terms and conditions?
							</Typography>
							<Button type="button" onClick={agreeHandler}>Yes, I agree</Button>
							<Button type="button" variant="plain" onClick={disagreeHandler}>No</Button>
						</Stack>
					</ModalDialog>
				</Modal>
				<Modal open={openConfirmationModal} onClose={() => setOpenConfirmationModal(false)}>
					<ModalDialog
						aria-labelledby="basic-modal-dialog-title"
						aria-describedby="basic-modal-dialog-description"
						sx={{ maxWidth: 500 }}
					>
						<Typography id="basic-modal-dialog-title" component="h2">
							Brand Manager
						</Typography>
						<Typography id="basic-modal-dialog-description" textColor="text.tertiary">
							Please verify the following information. Make changes if necessary.
						</Typography>
						<form
							onSubmit={async (event) => {
								event.preventDefault();
								setProcessingNewChanges(true);
								if (makeChanges) {
									await Api.updateTagOwner(commaSeparatedListOfTags, {
										ownerName: ownerFullName,
										ownerPhone: ownerPhone,
										ownerEmail: ownerEmail
									}).then(async () => {
										// refresh token
										Api.authorizeOnce(commaSeparatedListOfTags, accessCode)
											.then((_) => {
												setIsValidSession(true);
												setOpenConfirmationModal(false);
												setProcessingNewChanges(false);
											});
									});
								} else { setOpenConfirmationModal(false); }
							}
							}
						>
							<Stack spacing={2}>
								<FormControl>
									<FormLabel required>Full Name</FormLabel>
									<Input autoFocus disabled={!makeChanges} required value={ownerFullName} onChange={(e) => setOwnerFullName(e.target.value)} />
								</FormControl>
								<FormControl>
									<FormLabel required>Phone Number</FormLabel>
									<PhoneInput
										className="MuiInput-root MuiInput-variantOutlined MuiInput-colorNeutral MuiInput-sizeMd MuiInput-formControl CopyCat-input"
										disabled={!makeChanges}
										limitMaxLength={true}
										autoComplete="tel"
										useNationalFormatForDefaultCountryValue={true}
										defaultCountry="US"
										value={ownerPhone}
										onChange={setOwnerPhone} />
								</FormControl>
								<FormControl>
									<FormLabel required>Email Address</FormLabel>
									<Input type="email" required disabled={!makeChanges} value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)} />
								</FormControl>
								{!makeChanges && <Button type="submit">Looks good</Button>}
								{!makeChanges && <Button type="button" variant="plain" onClick={() => setMakeChanges(true)}>Make changes</Button>}
								{makeChanges && <Button type="submit" disabled={processingNewChanges || !canSubmitChanges}>Confirm changes</Button>}
							</Stack>
						</form>
					</ModalDialog>
				</Modal>
			</Container>
		</FullWidthLayout >
	);
};

export default BrandsCheckoutPage;
